import type { MetaFunction } from "react-router";
import { buildRouteMeta, SITE_DESCRIPTION } from "@/lib/seo";
import { AccessRequestButton } from "@/components/access-request-button";
import { StaticEndState } from "@/components/privacy-graph-scrolly/static-end-state";
import { PrivacyGraphScrolly } from "@/components/privacy-graph-scrolly";
import { SectionShell } from "@/components/section-shell";
import { ReframeGrid } from "@/components/reframe-grid";
import { CtaSection } from "@/components/cta-section";
import { EngineSection } from "@/components/engine-section";
import { SagePeriod } from "@/components/sage-period";
import { SectionMark } from "@/components/section-mark";
import { OperatingLoopTabs } from "@/components/operating-loop-tabs";

export const meta: MetaFunction = () => buildRouteMeta({
  title: "Keep up with constant change | Truspecta",
  description: SITE_DESCRIPTION,
  path: "/",
});

const META_STRIP = ["Read-only", "Agentless", "Metadata only"] as const;

export default function HomePage() {
  return (
    <>
      {/* §01 — Hero */}
      <section
        aria-labelledby="hero-heading"
        className="bg-background pt-20 pb-16 sm:pt-28 sm:pb-24"
      >
        <div className="max-w-[640px] lg:max-w-[800px] mx-auto px-6 text-center">
          <h1
            id="hero-heading"
            className="text-balance font-heading text-hero-sm font-semibold leading-[1.0] tracking-[-0.035em] text-foreground max-[420px]:text-[2.75rem] sm:text-hero-md lg:text-hero-xl"
          >
            Privacy teams need to keep up with systems that change <span className="whitespace-nowrap">daily<SagePeriod /></span>
          </h1>

          <p className="mt-5 text-xl text-foreground-muted leading-snug">
            Truspecta connects privacy work to real systems, so teams know what changed, what needs review, and what evidence supports it.
          </p>

          <div className="mt-8">
            <AccessRequestButton size="lg">Request access</AccessRequestButton>
          </div>

          <p
            aria-label="Product attributes"
            className="mt-10 text-[12px] font-mono text-foreground-muted"
          >
            {META_STRIP.map((word, i) => (
              <span key={word} className="inline-block">
                {i > 0 ? <span aria-hidden="true" className="opacity-50 px-2">·</span> : null}
                {word}
              </span>
            ))}
          </p>
        </div>
      </section>

      <SectionMark className="my-6 sm:my-10" />

      {/* §02 — Reframe */}
      <section
        aria-labelledby="reframe-heading"
        className="bg-background reframe-scroll-pin relative"
      >
        <div className="reframe-scroll-pin__inner">
          <div className="mx-auto w-full max-w-6xl px-6 py-12 sm:py-24">
            <ReframeGrid />
          </div>
        </div>
      </section>

      <SectionMark className="my-6 sm:my-10" />

      {/* §03 — Operating loop */}
      <SectionShell
        aria-labelledby="loop-heading"
        bg="paper"
        maxWidth="6xl"
        padding="default"
      >
        <div className="mx-auto mb-8 max-w-[780px] text-center sm:mb-10">
          <h2
            id="loop-heading"
            className="font-heading text-[2rem] font-semibold leading-[1.08] tracking-[-0.025em] text-foreground sm:text-[2.5rem]"
          >
            Turn constant change into privacy action
          </h2>
          <p className="mt-4 text-base leading-relaxed text-foreground-muted">
            Truspecta aligns privacy programs with the workloads they&rsquo;re meant to govern, so reviews follow real systems instead of point-in-time snapshots.
          </p>
        </div>

        <OperatingLoopTabs />
      </SectionShell>

      <SectionMark className="my-8 sm:my-12" />

      {/* §04 — The Privacy Graph (paper canvas) */}
      <SectionShell
        aria-labelledby="graph-heading"
        bg="secondary"
        maxWidth="wide"
        padding="default"
        data-scrolly-root
      >
        <div className="mx-auto max-w-[820px] text-center">
          <h2
            id="graph-heading"
            className="font-heading text-[2.25rem] font-semibold leading-[1.05] tracking-[-0.03em] text-foreground sm:text-display-section"
          >
            Build a live map of systems, data, and reviews
          </h2>
          <p className="mt-4 text-base leading-relaxed text-foreground-muted">
            From scattered spreadsheets to a live map of systems, data, and reviews.
          </p>
        </div>

        {/* SR-only narrative summary (spec §8) */}
        <p className="sr-only">
          Truspecta scans your AWS infrastructure read-only, mapping every store,
          every flow, and every AI integration. Resources cluster into named workloads.
          Drift routes a review to the workload owner. Findings surface exposure
          and avoidable spend. RoPAs draft directly from the graph.
        </p>

        <div className="mt-8 sm:mt-10">
          <PrivacyGraphScrolly />
          {/* Always-rendered SSR fallback — hidden by CSS when animated scrolly is active */}
          <StaticEndState />
        </div>
      </SectionShell>

      {/* §04 — Engine Capabilities (paper canvas, extends §03) */}
      <EngineSection />

      <SectionMark className="my-6 sm:my-10" />

      {/* §05 — Closing CTA */}
      <CtaSection
        heading="Help your privacy team keep up with constant change."
        subhead="Connect reviews, findings, drift, and evidence to the systems they govern."
        buttonText="Request access"
      />
    </>
  );
}
